import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { Calendar, Download, Link2, Copy, Check, ExternalLink, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { DoodleSquiggle } from './Doodles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CalendarSync() {
  const [feedUrl, setFeedUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  const generateFeedUrl = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/calendar/feed-url`, { withCredentials: true });
      setFeedUrl(data.feed_url);
    } catch (e) {
      toast.error('Failed to generate feed URL');
    } finally { setLoading(false); }
  }, []);

  const downloadIcs = async () => {
    try {
      const response = await axios.get(`${API}/calendar/export.ics`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'focus-critter.ics';
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Calendar file downloaded!');
    } catch (e) {
      toast.error('Failed to download');
    }
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(feedUrl);
    setCopied(true);
    toast.success('Feed URL copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6" data-testid="calendar-sync">
      <div className="flex items-center gap-3">
        <Calendar className="w-6 h-6" />
        <h2 className="text-2xl font-bold font-['Fredoka']">Calendar Sync</h2>
      </div>
      <DoodleSquiggle width={100} color="#A2D2FF" />

      {/* Download .ics */}
      <div className="neo-card p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl border-2 border-black bg-[#A2D2FF] flex items-center justify-center flex-shrink-0">
            <Download className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold font-['Fredoka'] text-lg mb-1">Download .ics File</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Download your schedule and tasks as an .ics file. Import it into Google Calendar, Apple Calendar, Outlook, or any calendar app.
            </p>
            <button onClick={downloadIcs} className="neo-btn px-5 py-2 bg-[#A2D2FF] flex items-center gap-2" data-testid="download-ics-btn">
              <Download className="w-4 h-4" />
              <span>Download .ics</span>
            </button>
          </div>
        </div>
      </div>

      {/* iCal Feed URL */}
      <div className="neo-card p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl border-2 border-black bg-[#B9FBC0] flex items-center justify-center flex-shrink-0">
            <Link2 className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold font-['Fredoka'] text-lg mb-1">Subscribe with iCal Feed</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Get a live-updating feed URL. Subscribe in your calendar app and it'll automatically stay in sync with Focus Critter.
            </p>

            {!feedUrl ? (
              <button onClick={generateFeedUrl} disabled={loading}
                className="neo-btn px-5 py-2 bg-[#B9FBC0] flex items-center gap-2" data-testid="generate-feed-btn">
                <Link2 className="w-4 h-4" />
                <span>{loading ? 'Generating...' : 'Generate Feed URL'}</span>
              </button>
            ) : (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <input type="text" value={feedUrl} readOnly
                    className="neo-input flex-1 text-xs font-mono" data-testid="feed-url-input" />
                  <button onClick={copyUrl}
                    className={`neo-btn px-4 py-2 ${copied ? 'bg-[#B9FBC0]' : 'bg-white'}`} data-testid="copy-feed-btn">
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* How to Subscribe */}
      <div className="neo-card p-6 bg-[#FDF0D5]">
        <h3 className="font-bold font-['Fredoka'] text-lg mb-3">How to Subscribe</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white dark:bg-[#252545] rounded-xl p-4 border-2 border-black">
            <div className="flex items-center gap-2 mb-2">
              <Smartphone className="w-5 h-5" />
              <h4 className="font-bold font-['Fredoka'] text-sm">Google Calendar</h4>
            </div>
            <ol className="text-xs text-gray-600 dark:text-gray-400 space-y-1 list-decimal list-inside">
              <li>Open Google Calendar on desktop</li>
              <li>Click "+" next to "Other calendars"</li>
              <li>Select "From URL"</li>
              <li>Paste your feed URL</li>
              <li>Click "Add calendar"</li>
            </ol>
          </div>
          <div className="bg-white dark:bg-[#252545] rounded-xl p-4 border-2 border-black">
            <div className="flex items-center gap-2 mb-2">
              <Smartphone className="w-5 h-5" />
              <h4 className="font-bold font-['Fredoka'] text-sm">Apple Calendar</h4>
            </div>
            <ol className="text-xs text-gray-600 dark:text-gray-400 space-y-1 list-decimal list-inside">
              <li>Open Calendar app</li>
              <li>File → New Calendar Subscription</li>
              <li>Paste your feed URL</li>
              <li>Click Subscribe</li>
              <li>Set auto-refresh interval</li>
            </ol>
          </div>
          <div className="bg-white dark:bg-[#252545] rounded-xl p-4 border-2 border-black">
            <div className="flex items-center gap-2 mb-2">
              <Smartphone className="w-5 h-5" />
              <h4 className="font-bold font-['Fredoka'] text-sm">Outlook</h4>
            </div>
            <ol className="text-xs text-gray-600 dark:text-gray-400 space-y-1 list-decimal list-inside">
              <li>Open Outlook Calendar</li>
              <li>Click "Add calendar"</li>
              <li>Select "Subscribe from web"</li>
              <li>Paste your feed URL</li>
              <li>Click Import</li>
            </ol>
          </div>
        </div>
      </div>

      {/* What's Included */}
      <div className="neo-card p-6">
        <h3 className="font-bold font-['Fredoka'] text-lg mb-3">What's Included</h3>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <span className="text-green-600">&#10003;</span>
            <span className="text-sm font-semibold">Tasks (with lane labels)</span>
          </div>
          <div className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <span className="text-green-600">&#10003;</span>
            <span className="text-sm font-semibold">Daily schedule items</span>
          </div>
          <div className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <span className="text-green-600">&#10003;</span>
            <span className="text-sm font-semibold">Recurring events</span>
          </div>
          <div className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <span className="text-green-600">&#10003;</span>
            <span className="text-sm font-semibold">Priority levels</span>
          </div>
        </div>
      </div>
    </div>
  );
}
